import { type MouseEvent, type Ref, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MdCopyAll } from 'react-icons/md';
import { toast } from 'react-toastify';
import { useCopyToClipboard } from 'react-use';

import type { ICustomFrontIcon } from '@components';

import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { TreeItem, type TreeItemProps } from '@mui/x-tree-view';

type CustomTreeItemProperties = {
  labelText: string;
  withCopy?: boolean;
  ref?: Ref<HTMLLIElement>;
  customFrontIcon: null | ICustomFrontIcon;
} & TreeItemProps;

export const CustomTreeItem = ({
  labelText,
  ref: reference,
  customFrontIcon,
  withCopy = false,
  ...other
}: CustomTreeItemProperties) => {
  const { t } = useTranslation();

  const [, copyTextToClipboard] = useCopyToClipboard();

  const handleCopy = useCallback(
    (event: MouseEvent<HTMLButtonElement>, label: string) => {
      event.stopPropagation();
      const formattedLabel = label?.split('(')[0].trim();
      copyTextToClipboard(formattedLabel ?? '');
      toast.success(
        t('common.toast.copied', { fieldName: t('common.campaign.name') })
      );
    },
    [copyTextToClipboard, t]
  );

  return (
    <TreeItem
      ref={reference}
      {...other}
      label={
        <>
          {customFrontIcon && (
            <Tooltip title={customFrontIcon.tooltip ?? ''}>
              <Stack
                sx={{
                  marginRight: '0.5rem',
                }}
              >
                {customFrontIcon.icon}
              </Stack>
            </Tooltip>
          )}
          <Typography>{labelText}</Typography>

          {withCopy && (
            <IconButton
              onClick={(event) => {
                handleCopy(event, labelText);
              }}
              sx={{
                opacity: '0',
                padding: '0',
                transition: '.4s',
                width: '1.375rem',
                height: '1.375rem',
                marginLeft: '0.5rem',
              }}
              color="secondary"
            >
              <MdCopyAll />
            </IconButton>
          )}
        </>
      }
    />
  );
};
